// Dependency-free storage helpers for the reseller "view this company" switcher.
// Separate from selectedCompany.ts (which is dedicated to the Machines-only switcher)
// so the two independent mechanisms never interfere with each other.

export interface Company {
  tenantId: number;
  name: string;
}

const STORAGE_KEY = 'resellerViewedCompany';

// Sentinel "company" meaning the reseller's merged "all my companies" view (their own
// workspace + every company they created). tenantId -1 is never a real tenant id
// (ids are positive user ids), so it stores and round-trips through getViewedCompany
// like any other selection; api.ts turns it into the `x-reseller-scope: all` header
// instead of impersonating a single company.
export const RESELLER_ALL_TENANT_ID = -1;
export const RESELLER_ALL_COMPANY: Company = { tenantId: RESELLER_ALL_TENANT_ID, name: 'All my companies' };
export const isResellerAllScope = (): boolean => getViewedCompany()?.tenantId === RESELLER_ALL_TENANT_ID;

export const getViewedCompany = (): Company | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.tenantId === 'number') return parsed as Company;
    return null;
  } catch {
    return null;
  }
};

export const storeViewedCompany = (company: Company | null): void => {
  if (company) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(company));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
};

export const clearViewedCompany = (): void => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(FILTER_KEY);
};

// Within the merged "all my companies" view, an optional narrow-to-one-company filter.
// Stored separately so api.ts (outside React) can read it for the request header; the
// backend only honours it for tenants the reseller actually owns.
const FILTER_KEY = 'resellerCompanyFilter';

export const getResellerCompanyFilter = (): number | null => {
  try {
    const raw = localStorage.getItem(FILTER_KEY);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  } catch {
    return null;
  }
};

export const storeResellerCompanyFilter = (tenantId: number | null): void => {
  if (tenantId == null) localStorage.removeItem(FILTER_KEY);
  else localStorage.setItem(FILTER_KEY, String(tenantId));
};
